/**
 * VideoUploadNode (Canvas V2)
 *
 * Upload a local video file. Supports file picker and drag-and-drop.
 * 100MB limit, backend API upload, VideoPreviewPlayer for preview.
 * FloatingNodeLabel (editable) above the node, hidden output port.
 *
 * Uses a custom card wrapper matching ImageUploadNode's clean,
 * media-focused card style with boxShadow for state indication.
 * No BaseNode header bar — just the video content.
 *
 * 5 visual states:
 *   1. Empty — clickable label, upload icon + "Upload Video"
 *   2. Uploading — video preview + overlay spinner
 *   3. Error — error icon + message + retry hint
 *   4. Success — clean video only (VideoPreviewPlayer)
 *   5. Hover — overlay buttons (expand, download, replace)
 *
 * Output: out_video (video)
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { NodeProps, Position } from 'reactflow'
import { createPortal } from 'react-dom'
import {
  FloatingNodeLabel,
  VideoPreviewPlayer,
  ASPECT_WIDTH_MAP,
  snapToClosestAspectWidth,
} from '../shared'
import type { VideoPreviewPlayerHandle } from '../shared/VideoPreviewPlayer'
import { TypedHandle } from '../../TypedHandle'
import { useCanvasOptional } from '../../CanvasContext'
import { useFileUpload } from '../shared/useFileUpload'
import { api } from '../../../../api/client'
import { useProjectId } from '../../../../contexts/ProjectIdContext'

export interface VideoUploadNodeData {
  out_video?: string
  in_file_name?: string
  status?: 'idle' | 'running' | 'completed' | 'error'
  error?: string
  label?: string
  /** File object passed on node creation for auto-upload */
  initialFile?: File
  /** Eagerly-created blob URL for instant preview */
  _initialPreviewUrl?: string
}

const VideoUploadNode = memo(({ id, data, selected }: NodeProps<VideoUploadNodeData>) => {
  const {
    out_video,
    in_file_name,
    status = 'idle',
    error,
  } = data

  const canvas = useCanvasOptional()
  const projectId = useProjectId()
  const [isHovering, setIsHovering] = useState(false)
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)

  // Track whether the label was manually set by the user
  const labelManuallySet = useRef(false)

  // Local state for immediate label display (Issue 2: frontend-origin YDoc
  // writes skip React state rebuild, so we need local state for instant feedback)
  const [derivedLabel, setDerivedLabel] = useState<string>('')

  const updateNodeData = useCallback(
    (updates: Record<string, unknown>) => {
      // Auto-set label from uploaded filename (if not manually renamed)
      const merged = { ...updates }
      if (updates.in_file_name && typeof updates.in_file_name === 'string' && !labelManuallySet.current) {
        const nameWithoutExt = (updates.in_file_name as string).replace(/\.[^.]+$/, '')
        merged.label = nameWithoutExt
        setDerivedLabel(nameWithoutExt)
      }
      canvas?.updateNodeData(id, merged)
    },
    [id, canvas]
  )

  const {
    previewUrl,
    isUploading,
    handleFileChange,
    dragHandlers,
    isDragOver,
    retry,
    multiFileWarning,
    uploadFile,
  } = useFileUpload({
    acceptMime: 'video',
    updateNodeData,
    persistedValue: out_video,
    initialPreviewUrl: data._initialPreviewUrl,
  })

  // Auto-upload initialFile when canvas context is ready
  const initialFileProcessed = useRef(false)
  useEffect(() => {
    if (data.initialFile && canvas && !initialFileProcessed.current) {
      initialFileProcessed.current = true
      uploadFile(data.initialFile)
    }
  }, [data.initialFile, canvas, uploadFile])

  // Clear initialFile from node data after processing
  const initialFileCleared = useRef(false)
  useEffect(() => {
    if (data.initialFile && !initialFileCleared.current) {
      initialFileCleared.current = true
      setTimeout(() => {
        canvas?.updateNodeData(id, { initialFile: undefined })
      }, 100)
    }
  }, [data.initialFile, id, canvas])

  const persistedUrl = out_video ? api.getAssetUrl(projectId, out_video) : undefined
  const displayUrl = previewUrl || persistedUrl

  // Dynamic aspect ratio from loaded video
  const [aspectRatio, setAspectRatio] = useState<string | null>(null)
  // Node width snapped to closest ASPECT_WIDTH_MAP bucket; default 16:9 = 315px
  const [nodeWidth, setNodeWidth] = useState(ASPECT_WIDTH_MAP['16:9'])

  // VideoPreviewPlayer ref for accessing video dimensions
  const playerRef = useRef<VideoPreviewPlayerHandle>(null)

  // Read video dimensions once metadata is loaded
  useEffect(() => {
    if (!displayUrl || isUploading) return

    const checkDimensions = () => {
      const video = playerRef.current?.videoElement
      if (!video) return
      if (video.videoWidth && video.videoHeight) {
        setAspectRatio(`${video.videoWidth}/${video.videoHeight}`)
        setNodeWidth(snapToClosestAspectWidth(video.videoWidth, video.videoHeight))
        return
      }
      // Listen for loadedmetadata if dimensions not ready yet
      const handleMeta = () => {
        if (video.videoWidth && video.videoHeight) {
          setAspectRatio(`${video.videoWidth}/${video.videoHeight}`)
          setNodeWidth(snapToClosestAspectWidth(video.videoWidth, video.videoHeight))
        }
      }
      video.addEventListener('loadedmetadata', handleMeta, { once: true })
      return () => video.removeEventListener('loadedmetadata', handleMeta)
    }

    // Small delay to let VideoPreviewPlayer mount the video element
    const timer = setTimeout(checkDimensions, 50)
    return () => clearTimeout(timer)
  }, [displayUrl, isUploading])

  // Reset aspect ratio and node width when video is cleared
  useEffect(() => {
    if (!displayUrl) {
      setAspectRatio(null)
      setNodeWidth(ASPECT_WIDTH_MAP['16:9'])
    }
  }, [displayUrl])

  const handleDownload = useCallback(() => {
    if (!displayUrl) return
    const a = document.createElement('a')
    a.href = displayUrl
    a.download = in_file_name || 'video.mp4'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }, [displayUrl, in_file_name])

  // Escape to close lightbox
  useEffect(() => {
    if (!isVideoModalOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setIsVideoModalOpen(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [isVideoModalOpen])

  const handleRename = useCallback(
    (newName: string) => {
      labelManuallySet.current = true
      setDerivedLabel(newName)
      canvas?.updateNodeData(id, { label: newName })
    },
    [id, canvas]
  )

  const inputId = `file-upload-${id}`
  const isError = status === 'error'
  const hasError = isError && !!error

  return (
    <>
      <div className="relative">
        {/* Floating editable label above the card */}
        <FloatingNodeLabel
          name={derivedLabel || data.label || ''}
          fallback="Untitled"
          onRename={handleRename}
        />

        {/* Card wrapper -- matches ImageUploadNode's boxShadow pattern */}
        <div
          className="rounded-lg overflow-hidden"
          style={{
            width: nodeWidth,
            boxShadow: selected
              ? '0 0 0 2px rgba(255,255,255,0.7)'
              : hasError
                ? '0 0 0 1px rgba(239,68,68,0.5)'
                : '0 2px 8px rgba(0,0,0,0.3)',
          }}
          {...dragHandlers}
        >
          {/* Hidden file input */}
          <input
            type="file"
            accept="video/*"
            onChange={handleFileChange}
            className="hidden"
            id={inputId}
            disabled={isUploading}
          />

          {/* Main content area -- 5 visual states */}
          {isUploading && displayUrl ? (
            /* -- State 2: Uploading -- video preview + overlay spinner -- */
            <div
              className="relative"
              style={{ aspectRatio: aspectRatio || '16/9', overflow: 'hidden' }}
            >
              <video
                src={displayUrl}
                className="w-full h-full object-cover block"
                muted
                playsInline
              />
              <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center">
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span className="text-xxs text-white/80 mt-2">Uploading video...</span>
              </div>
            </div>
          ) : hasError ? (
            /* -- State 3: Error -- icon + message + retry -- */
            <div
              className="w-full bg-canvas flex flex-col items-center justify-center px-6 cursor-pointer"
              style={{ aspectRatio: '16/9' }}
              onClick={retry}
            >
              <svg
                className="w-8 h-8 text-red-400/60"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                />
              </svg>
              <span
                className="text-xxs text-red-400/80 mt-1.5 text-center line-clamp-2 cursor-help"
                title={error}
              >
                Failed to upload
              </span>
              <span className="text-xxs text-foreground/20 mt-1">Click to retry</span>
            </div>
          ) : displayUrl ? (
            /* -- State 4 + 5: Success + Hover -- VideoPreviewPlayer with overlay buttons -- */
            <div
              className="relative"
              style={{ aspectRatio: aspectRatio || '16/9', overflow: 'hidden' }}
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              <VideoPreviewPlayer
                ref={playerRef}
                videoUrl={displayUrl}
                isHovering={isHovering}
                showExpand={false}
                showDownload={false}
              />

              {/* -- Hover overlay buttons: expand, download, replace -- */}
              <div
                className={`absolute top-2 right-2 flex gap-1 z-10 transition-opacity ${
                  isHovering ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
              >
                {/* Expand */}
                <button
                  onClick={(e) => { e.stopPropagation(); setIsVideoModalOpen(true) }}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
                  title="Expand"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M20 8V4m0 0h-4M4 16v4m0 0h4M20 16v4m0 0h-4" />
                  </svg>
                </button>
                {/* Download */}
                <button
                  onClick={(e) => { e.stopPropagation(); handleDownload() }}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
                  title="Download"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </button>
                {/* Replace -- label triggers file picker */}
                <label
                  htmlFor={inputId}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors cursor-pointer nodrag"
                  title="Replace"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </label>
              </div>
            </div>
          ) : (
            /* -- State 1: Empty -- clickable upload area -- */
            <label
              htmlFor={inputId}
              className={`w-full bg-canvas flex flex-col items-center justify-center cursor-pointer transition-colors ${
                isDragOver
                  ? 'bg-primary/5 ring-2 ring-primary/40 ring-inset'
                  : ''
              }`}
              style={{ aspectRatio: '16/9' }}
            >
              {isDragOver ? (
                <span className="text-xs text-primary">Drop to upload</span>
              ) : (
                <>
                  <svg
                    className="w-8 h-8 text-foreground/15"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="text-xxs text-foreground/30 mt-1">Upload Video</span>
                </>
              )}
            </label>
          )}

          {/* Multi-file drop warning */}
          {multiFileWarning && (
            <div className="px-2 py-1.5 bg-amber-500/10 border-t border-amber-500/30">
              <span className="text-xxs text-amber-400">{multiFileWarning}</span>
            </div>
          )}
        </div>

        {/* Output handle: out_video -- visually hidden, kept in DOM for edges */}
        <TypedHandle
          type="source"
          position={Position.Right}
          id="out_video"
          portType="video"
          style={{ top: '50%', width: 0, height: 0, border: 'none', background: 'transparent', minWidth: 0, minHeight: 0 }}
        />
      </div>

      {/* Video Lightbox */}
      {isVideoModalOpen && displayUrl &&
        createPortal(
          <div
            data-testid="video-lightbox-backdrop"
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
            onClick={() => setIsVideoModalOpen(false)}
          >
            <div
              data-testid="video-lightbox"
              className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="video-lightbox-close"
                onClick={() => setIsVideoModalOpen(false)}
                className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
              >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <video
                src={displayUrl}
                controls
                autoPlay
                className="max-w-full max-h-[85vh] rounded-lg shadow-2xl bg-black"
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  )
})

VideoUploadNode.displayName = 'VideoUploadNode'
export default VideoUploadNode
